/* ============================================================
   Venturi anchor scrolling (2026-08-25)

   THE PROBLEM THIS SOLVES

   Clicking "The science" (or any in-page anchor) landed the visitor on
   the wrong section. The link and the target were both correct: the
   nav item is "#science" and sections/science-cards.liquid does carry
   id="science". What was wrong was the page height at the moment of
   the scroll.

   Several blocks above the science cards reserve NO space until their
   media loads:

     - sections/system-001-bundle.liquid renders the packshot with
       loading="lazy" and no width/height, under CSS that is
       width:100% / height:auto. A 4000x2800 PNG in a 960px slot is
       about 670px of height that does not exist until it arrives.
     - sections/intervention.liquid renders a <video preload="none"
       data-vu-lazyvideo> with no dimensions, whose <source> is only
       injected by the IntersectionObserver in snippets/vu-lazy-video
       once it nears the viewport.

   So the browser scrolls to where #science is RIGHT NOW, that scroll
   drags the collapsed content into view, the media loads, the page
   grows above the target, and the section the visitor asked for is
   pushed hundreds of pixels below where they were put down.

   The deferred cross-page handler at the end of assets/main.js has the
   same flaw from the other direction: it measures at load + 500ms,
   which is before any below-the-fold lazy content has been asked for.

   THE FIX

   Scroll, then keep re-measuring the target for a short window and
   re-issue the scroll whenever it has moved. Layout shifts caused by
   the scroll itself are therefore corrected instead of inherited. The
   loop stands down the moment the visitor touches the wheel, the
   screen or the keyboard, so it can never fight a person.

   This also applies the fixed header's clearance, which the browser's
   native anchor jump does not: the header is position:fixed, so a raw
   jump puts the section's tagline underneath it.

   RELATIONSHIP TO assets/main.js

   This file loads BEFORE main.js (see layout/theme.liquid) and claims
   window.__deferredAnchorHash, the value the inline script in the head
   of layout/theme.liquid captures on a cross-page anchor navigation.
   Claiming it means setting it to null, which is read as "nothing to
   do" by the older single-shot handler at the end of main.js, so that
   block stands itself down and only one scroll is ever scheduled.

   The same-page click handler here is deliberately scoped to the
   navigation surfaces plus the hero buttons. It is NOT a blanket
   a[href^="#"] handler, because several "#" links in this theme are
   load-bearing controls with their own preventDefault handlers -- the
   video tile in sections/story.liquid opens a modal from
   snippets/youtube-modal.liquid, the accordions and tab strips in
   main.js are the same shape. A bare "#" is ignored here for the same
   reason, and so is any anchor whose target does not exist.
   ============================================================ */

(function () {
  'use strict';

  // How long to keep watching for the target to move, and how often.
  var SETTLE_WINDOW_MS = 2500;
  var SETTLE_INTERVAL_MS = 200;

  // Breathing room below the fixed header, and how far the target has to
  // move before a correcting scroll is worth issuing.
  var EXTRA_PADDING_PX = 20;
  var TOLERANCE_PX = 4;

  // Where a same-page anchor click is honoured. See the note above on why
  // this is a list and not a[href^="#"].
  var NAV_SCOPE = [
    '.hero__btn a',
    '.nav a',
    '.menu-drawer__top a',
    '.footer__items a',
    '.footer__bottom-items a'
  ].join(', ');

  function headerClearance() {
    var header = document.querySelector('.header');
    if (!header) return 0;
    // offsetHeight ignores the transform headerSticky() in main.js uses to
    // slide the bar out of view, which is what we want: the header can come
    // back at any moment and the section must not be under it when it does.
    return header.offsetHeight + 14;
  }

  function desiredTop(target) {
    var top = window.pageYOffset
              + target.getBoundingClientRect().top
              - headerClearance()
              - EXTRA_PADDING_PX;
    return Math.max(0, Math.round(top));
  }

  function scrollToAnchor(target) {
    var cancelled = false;
    var lastTop = null;
    var deadline = Date.now() + SETTLE_WINDOW_MS;

    function stopWatching() {
      window.removeEventListener('wheel', cancel);
      window.removeEventListener('touchstart', cancel);
      window.removeEventListener('keydown', cancel);
    }

    function cancel() {
      cancelled = true;
      stopWatching();
    }

    // Hand control straight back if the visitor starts steering.
    window.addEventListener('wheel', cancel, { passive: true });
    window.addEventListener('touchstart', cancel, { passive: true });
    window.addEventListener('keydown', cancel);

    function settle() {
      if (cancelled) return;

      var top = desiredTop(target);

      // Only re-issue the scroll when the TARGET has moved. Comparing
      // against the current scroll position instead would restart the
      // easing on every tick while the smooth scroll is still travelling.
      if (lastTop === null || Math.abs(top - lastTop) > TOLERANCE_PX) {
        lastTop = top;
        window.scrollTo({ top: top, behavior: 'smooth' });
      }

      if (Date.now() < deadline) {
        setTimeout(settle, SETTLE_INTERVAL_MS);
      } else {
        stopWatching();
      }
    }

    settle();
  }

  function resolve(hash) {
    if (!hash || hash.charAt(0) !== '#' || hash.length < 2) return null;
    try {
      return document.querySelector(hash);
    } catch (err) {
      // A hash that is not a valid selector, e.g. "#1".
      return null;
    }
  }

  function restoreHash(hash) {
    try {
      history.replaceState(null, '', location.pathname + location.search + hash);
    } catch (err) {}
  }

  // ---- same-page clicks ----
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!e.target || !e.target.closest) return;

    var link = e.target.closest(NAV_SCOPE);
    if (!link) return;

    var hash = link.getAttribute('href');
    var target = resolve(hash);
    if (!target) return;

    e.preventDefault();
    restoreHash(hash);
    scrollToAnchor(target);
  });

  // ---- arriving from another page ----
  // layout/theme.liquid strips the hash on load so the browser cannot make
  // its own premature jump, and parks it here.
  var deferredHash = window.__deferredAnchorHash;
  if (!deferredHash) return;
  window.__deferredAnchorHash = null;

  function runDeferred() {
    // A beat after load so sections that inject markup on DOM ready are in
    // before the first measurement. The settle loop covers the rest.
    setTimeout(function () {
      var target = resolve(deferredHash);
      restoreHash(deferredHash);
      if (target) scrollToAnchor(target);
    }, 250);
  }

  if (document.readyState === 'complete') {
    runDeferred();
  } else {
    window.addEventListener('load', runDeferred, { once: true });
  }
})();
